import React, {useCallback, useEffect, useRef, useState} from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";

type Props = {
    label: string;
    fetchLogo: () => Promise<Blob | null>;
    onUpload: (file: File) => Promise<void>;
    onDelete?: () => Promise<void>;
    accept?: string;
};

const LightbulbToggle: React.FC<{dark: boolean; onToggle: () => void}> = ({dark, onToggle}) => (
    <button
        type="button"
        title="Toggle dark background"
        onClick={onToggle}
        className="absolute top-3 right-3 p-0.5 rounded transition-colors"
    >
        {dark
            ? <LightbulbIcon style={{fontSize: 22}} className="text-white"/>
            : <LightbulbOutlinedIcon style={{fontSize: 22}} className="text-light-grey"/>
        }
    </button>
);

const LogoUpload: React.FC<Props> = ({
    label,
    fetchLogo,
    onUpload,
    onDelete,
    accept = "image/png,image/jpeg,image/webp",
}) => {
    const [logoObjectURL, setLogoObjectURL] = useState<string | null>(null);
    const [darkBg, setDarkBg] = useState(false);

    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [previewURL, setPreviewURL] = useState<string | null>(null);
    const [previewDark, setPreviewDark] = useState(false);

    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [removeConfirm, setRemoveConfirm] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    const loadLogo = useCallback(async () => {
        const blob = await fetchLogo().catch(() => null);
        if (logoObjectURL) URL.revokeObjectURL(logoObjectURL);
        if (blob) {
            setLogoObjectURL(URL.createObjectURL(blob));
        } else {
            setLogoObjectURL(null);
        }
    }, [fetchLogo]);

    useEffect(() => {
        loadLogo();
        return () => { if (logoObjectURL) URL.revokeObjectURL(logoObjectURL); };
    }, []);

    useEffect(() => {
        return () => { if (previewURL) URL.revokeObjectURL(previewURL); };
    }, [previewURL]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (previewURL) URL.revokeObjectURL(previewURL);
            setPreviewFile(file);
            setPreviewURL(URL.createObjectURL(file));
            setPreviewDark(false);
        }
        e.target.value = "";
    };

    const handleUpload = useCallback(async () => {
        if (!previewFile) return;
        setError(null);
        setUploadProgress(0);
        setUploading(true);
        try {
            await onUpload(previewFile);
            setPreviewFile(null);
            if (previewURL) URL.revokeObjectURL(previewURL);
            setPreviewURL(null);
            await loadLogo();
        } catch (e: any) {
            const msg = e?.response?.data?.errors?.file?.[0] ?? e?.response?.data?.error;
            setError(msg ?? "Error uploading logo");
        } finally {
            setUploading(false);
        }
    }, [previewFile, previewURL, onUpload, loadLogo]);

    const handleRemove = useCallback(async () => {
        if (!onDelete) return;
        setRemoving(true);
        try {
            await onDelete();
            if (logoObjectURL) URL.revokeObjectURL(logoObjectURL);
            setLogoObjectURL(null);
            setRemoveConfirm(false);
        } catch {
            // silently ignore
        } finally {
            setRemoving(false);
        }
    }, [onDelete, logoObjectURL]);

    const hasLogo = !!logoObjectURL;

    return (
        <div className="bg-white shadow-md rounded-xl p-4 flex flex-col gap-3">
            <label className="font-semibold text-sm">{label}</label>

            {hasLogo && (
                <div className={`relative flex items-center justify-center rounded-md border border-light-light-grey h-24 transition-colors ${darkBg ? "bg-dark-dark-grey" : "bg-light-light-grey bg-opacity-30"}`}>
                    <LightbulbToggle dark={darkBg} onToggle={() => setDarkBg(v => !v)}/>
                    <img src={logoObjectURL!} alt="Logo" className="max-h-full max-w-full object-contain p-2"/>
                </div>
            )}

            {!hasLogo && !uploading && (
                <p className="text-sm text-light-grey">No logo uploaded</p>
            )}

            {uploading && (
                <div className="w-full bg-light-light-grey rounded-full h-2 overflow-hidden">
                    <div className="bg-blue h-2 rounded-full transition-all duration-200" style={{width: `${uploadProgress}%`}}/>
                </div>
            )}

            {previewFile && previewURL && (
                <div className="flex flex-col gap-1">
                    <p className="text-xs text-light-grey">Preview</p>
                    <div className={`relative flex items-center justify-center rounded-md border border-light-light-grey h-24 transition-colors ${previewDark ? "bg-dark-dark-grey" : "bg-light-light-grey bg-opacity-30"}`}>
                        <LightbulbToggle dark={previewDark} onToggle={() => setPreviewDark(v => !v)}/>
                        <img src={previewURL} alt="Preview" className="max-h-full max-w-full object-contain p-2"/>
                    </div>
                </div>
            )}

            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={handleFileChange}
            />

            <div className="flex gap-2 flex-wrap">
                <button
                    type="button"
                    className="px-4 h-9 rounded-md bg-blue text-white text-sm font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50"
                    disabled={uploading || removing}
                    onClick={() => inputRef.current?.click()}
                >
                    {hasLogo ? "Replace Logo" : "Upload Logo"}
                </button>

                {previewFile && (
                    <button
                        type="button"
                        className="px-4 h-9 rounded-md border border-blue text-blue text-sm font-medium hover:bg-desaturated-grey transition-colors disabled:opacity-50"
                        disabled={uploading || removing}
                        onClick={handleUpload}
                    >
                        {uploading ? "Uploading…" : "Save"}
                    </button>
                )}

                {hasLogo && onDelete && (
                    <button
                        type="button"
                        className="flex items-center gap-1.5 px-3 h-9 rounded-md border border-red text-red text-sm font-medium hover:bg-red hover:text-white transition-colors disabled:opacity-50"
                        disabled={uploading || removing}
                        onClick={() => setRemoveConfirm(true)}
                    >
                        <DeleteIcon style={{fontSize: 16}}/>
                        Remove
                    </button>
                )}
            </div>

            {error && <p className="text-sm text-red">{error}</p>}

            {removeConfirm && (
                <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4">
                    <div className="w-full max-w-sm bg-white rounded-xl border border-light-light-grey shadow-xl p-5 flex flex-col gap-4">
                        <p className="font-semibold">Remove logo?</p>
                        <p className="text-sm text-light-grey">This will permanently delete the uploaded logo.</p>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setRemoveConfirm(false)}
                                className="h-10 rounded-md border border-light-light-grey bg-white hover:bg-desaturated-grey transition text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleRemove}
                                disabled={removing}
                                className="h-10 rounded-md bg-red text-white hover:bg-opacity-90 transition text-sm disabled:opacity-50"
                            >
                                {removing ? "Removing…" : "Remove"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogoUpload;
